// External
import { useCallback } from "react";
import _ from "lodash";

// Internal
import { GRADING_PERIOD_COLUMNS_SECTION_ID } from "../../../modules/constants";
import {
  toggleRowChildItems,
  toggleItemWithChildren,
  updateLabelValue,
} from "../../../modules/utils";

// Relative
import {
  buildNewColumnItem,
  getEnabledKeys,
} from "../modules/defaultSettingsContentUtils";
import { resolveSelectableKeyForItemId } from "../modules/viewModelHelpers";

export const useListItemHandlers = ({
  updateList,
  gradingPeriods,
  gradeScaleDescriptionMap,
  transcriptToSourceIdMap,
  language,
}) => {
  const getBaseGradeScaleId = useCallback(
    itemId => {
      const matchedPeriod = _.find(gradingPeriods, ({ gradingPeriod }) =>
        _.startsWith(itemId, `${_.get(gradingPeriod, "id", "")}_`)
      );

      if (!matchedPeriod) return itemId;

      const prefix = `${_.get(matchedPeriod, "gradingPeriod.id", "")}_`;
      return _.replace(itemId, prefix, "");
    },
    [gradingPeriods]
  );

  const handleToggleItem = useCallback(
    ({ listKey, id }) => {
      updateList({
        listKey,
        updater: current =>
          _.map(current, item => {
            if (item.id !== id) return item;
            if (item.isCheckable === false) return item;
            return toggleItemWithChildren({
              item,
              isEnabled: !item.isEnabled,
            });
          }),
      });
    },
    [updateList]
  );

  const handleLabelUpdate = useCallback(
    ({ listKey, id, content }) => {
      updateList({
        listKey,
        updater: current =>
          _.map(current, item => {
            if (item.id !== id) return item;
            const updatedLabel = updateLabelValue({
              currentLabel: item.label,
              newValue: content,
              language,
            });
            return { ...item, content, label: updatedLabel };
          }),
      });
    },
    [language, updateList]
  );

  const handleReorder = useCallback(
    ({ listKey, updatedItems }) => {
      updateList({
        listKey,
        updater: () => updatedItems,
      });
    },
    [updateList]
  );

  const handleReorderForGradingPeriod = useCallback(
    ({ listKey, updatedItems, gradingPeriodPrefix }) => {
      if (!gradingPeriodPrefix) {
        handleReorder({ listKey, updatedItems });
        return;
      }

      updateList({
        listKey,
        updater: current => {
          const startIndex = _.findIndex(current, item =>
            _.startsWith(_.get(item, "id", ""), gradingPeriodPrefix)
          );
          const endIndex = _.findLastIndex(current, item =>
            _.startsWith(_.get(item, "id", ""), gradingPeriodPrefix)
          );

          if (startIndex === -1 || endIndex === -1) {
            return current;
          }

          return [
            ..._.slice(current, 0, startIndex),
            ...updatedItems,
            ..._.slice(current, endIndex + 1),
          ];
        },
      });
    },
    [handleReorder, updateList]
  );

  const handleChildToggle = useCallback(
    ({ listKey, parentId, childId }) => {
      updateList({
        listKey,
        updater: current =>
          toggleRowChildItems({
            currentItems: current,
            parentId,
            childId,
          }),
      });
    },
    [updateList]
  );

  const handleDeleteManageColumnItem = useCallback(
    ({ listKey, id }) => {
      updateList({
        listKey,
        updater: current => _.filter(current, item => item.id !== id),
      });
    },
    [updateList]
  );

  const updateManageColumnsSelection = useCallback(
    ({ listKey, gradingPeriodPrefix, selectedKeys, keyToOptionMap }) => {
      updateList({
        listKey,
        updater: current => {
          const groupItems = gradingPeriodPrefix
            ? _.filter(current, item =>
                _.startsWith(_.get(item, "id", ""), gradingPeriodPrefix)
              )
            : current;
          const otherItems = gradingPeriodPrefix
            ? _.filter(
                current,
                item =>
                  !_.startsWith(_.get(item, "id", ""), gradingPeriodPrefix)
              )
            : [];

          const groupItemMap = _.keyBy(groupItems, "id");
          const selectedKeySet = new Set(selectedKeys);
          const selectableKeySet = new Set(_.keys(keyToOptionMap));

          const updatedGroupItems = _.compact(
            _.map(groupItems, item => {
              const itemId = _.get(item, "id", "");
              const selectableItemKey = resolveSelectableKeyForItemId({
                itemId,
                gradingPeriodPrefix,
                transcriptToSourceIdMap,
                selectableKeySet,
              });
              const isSelectable = Boolean(selectableItemKey);

              if (!isSelectable) {
                return item;
              }

              if (!selectedKeySet.has(selectableItemKey)) {
                return null;
              }

              const option = _.get(keyToOptionMap, selectableItemKey, {});
              const persistedItemId = _.get(option, "persistedKey", itemId);

              return {
                ...item,
                id: persistedItemId,
                isEnabled: true,
                isManageColumnItem: true,
              };
            })
          );

          const mappedKeysInUpdatedGroup = new Set(
            _.compact(
              _.map(updatedGroupItems, item =>
                resolveSelectableKeyForItemId({
                  itemId: _.get(item, "id", ""),
                  gradingPeriodPrefix,
                  transcriptToSourceIdMap,
                  selectableKeySet,
                })
              )
            )
          );

          const newItems = _.compact(
            _.map(selectedKeys, key => {
              if (groupItemMap[key] || mappedKeysInUpdatedGroup.has(key)) {
                return null;
              }
              const option = _.get(keyToOptionMap, key, {});
              const baseId = gradingPeriodPrefix
                ? _.replace(key, gradingPeriodPrefix, "")
                : getBaseGradeScaleId(key);
              const persistedKey = _.get(option, "persistedKey", key);

              return buildNewColumnItem({
                id: persistedKey,
                label: _.get(option, "label", key),
                isManageColumnItem: true,
                description:
                  listKey === GRADING_PERIOD_COLUMNS_SECTION_ID
                    ? _.get(gradeScaleDescriptionMap, baseId, "")
                    : "",
              });
            })
          );

          return gradingPeriodPrefix
            ? [...otherItems, ...updatedGroupItems, ...newItems]
            : [...updatedGroupItems, ...newItems];
        },
      });
    },
    [
      getBaseGradeScaleId,
      gradeScaleDescriptionMap,
      transcriptToSourceIdMap,
      updateList,
    ]
  );

  return {
    handleToggleItem,
    handleLabelUpdate,
    handleReorder,
    handleReorderForGradingPeriod,
    handleChildToggle,
    handleDeleteManageColumnItem,
    updateManageColumnsSelection,
    getEnabledKeys,
  };
};
